import { useCallback, useEffect, useRef, useState } from 'react'

export type AutoRefreshReason = 'mount' | 'manual' | 'visibility' | 'focus' | 'interval'

export interface AutoRefreshInvocation {
  reason: AutoRefreshReason
}

interface TriggerRefreshOptions {
  force?: boolean
}

interface UseAutoRefreshOnForegroundOptions {
  refresh: (invocation: AutoRefreshInvocation) => Promise<void>
  enabled?: boolean
  staleAfterMs: number
  pollIntervalMs: number
  minRefreshGapMs: number
  maxFailureBackoffMs: number
}

interface UseAutoRefreshOnForegroundResult {
  triggerRefresh: (
    reason?: AutoRefreshReason,
    options?: TriggerRefreshOptions,
  ) => Promise<boolean>
  lastSuccessAt: number | null
  lastAttemptAt: number | null
  lastError: string | null
  isRefreshing: boolean
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message
  }
  return 'Refresh failed.'
}

export function useAutoRefreshOnForeground({
  refresh,
  enabled = true,
  staleAfterMs,
  pollIntervalMs,
  minRefreshGapMs,
  maxFailureBackoffMs,
}: UseAutoRefreshOnForegroundOptions): UseAutoRefreshOnForegroundResult {
  const refreshRef = useRef(refresh)
  const lastSuccessAtRef = useRef<number | null>(null)
  const inFlightRefreshRef = useRef<Promise<boolean> | null>(null)
  const nextAllowedAttemptAtRef = useRef(0)
  const failureCountRef = useRef(0)
  const [lastSuccessAt, setLastSuccessAt] = useState<number | null>(null)
  const [lastAttemptAt, setLastAttemptAt] = useState<number | null>(null)
  const [lastError, setLastError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    refreshRef.current = refresh
  }, [refresh])

  const isStale = useCallback(() => {
    const lastSuccessAtValue = lastSuccessAtRef.current
    if (lastSuccessAtValue === null) {
      return true
    }
    return Date.now() - lastSuccessAtValue >= staleAfterMs
  }, [staleAfterMs])

  const triggerRefresh = useCallback(async (
    reason: AutoRefreshReason = 'manual',
    options: TriggerRefreshOptions = {},
  ): Promise<boolean> => {
    if (!enabled) {
      return false
    }

    const force = options.force === true
    const now = Date.now()

    if (!force && !isStale()) {
      return false
    }

    if (!force && now < nextAllowedAttemptAtRef.current) {
      return false
    }

    const lastSuccessAtValue = lastSuccessAtRef.current
    if (!force && lastSuccessAtValue !== null && now - lastSuccessAtValue < minRefreshGapMs) {
      return false
    }

    if (inFlightRefreshRef.current) {
      return inFlightRefreshRef.current
    }

    const refreshPromise = (async () => {
      setIsRefreshing(true)
      setLastAttemptAt(now)

      try {
        await refreshRef.current({ reason })
        const successAt = Date.now()
        lastSuccessAtRef.current = successAt
        setLastSuccessAt(successAt)
        setLastError(null)
        failureCountRef.current = 0
        nextAllowedAttemptAtRef.current = successAt + minRefreshGapMs
        return true
      } catch (error) {
        setLastError(toErrorMessage(error))
        failureCountRef.current += 1
        const backoffMultiplier = 2 ** Math.max(0, failureCountRef.current - 1)
        const backoffMs = Math.min(
          maxFailureBackoffMs,
          minRefreshGapMs * backoffMultiplier,
        )
        nextAllowedAttemptAtRef.current = Date.now() + backoffMs
        return false
      } finally {
        setIsRefreshing(false)
        inFlightRefreshRef.current = null
      }
    })()

    inFlightRefreshRef.current = refreshPromise
    return refreshPromise
  }, [
    enabled,
    isStale,
    maxFailureBackoffMs,
    minRefreshGapMs,
  ])

  useEffect(() => {
    if (!enabled) {
      return
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      void triggerRefresh('visibility')
    }

    const handleWindowFocus = () => {
      void triggerRefresh('focus')
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleWindowFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleWindowFocus)
    }
  }, [enabled, triggerRefresh])

  useEffect(() => {
    if (!enabled || pollIntervalMs <= 0) {
      return
    }

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return
      }
      void triggerRefresh('interval')
    }, pollIntervalMs)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [enabled, pollIntervalMs, triggerRefresh])

  return {
    triggerRefresh,
    lastSuccessAt,
    lastAttemptAt,
    lastError,
    isRefreshing,
  }
}

